import { z } from 'zod';
import type { AgentPrepared, AgentToolDef } from './tool-defs';

/**
 * email_me_report: email a report to the signed-in user's OWN address.
 * API: EmailMeController (api/v1/email-me) in the .NET Admin API.
 *
 * The model supplies only subject, intro and structured blocks. It never supplies a
 * recipient or HTML: the API always sends to the caller's own address and renders the
 * blocks itself. On non-production servers only (GET email-me/config says
 * overrideAllowed), the confirm card shows a "Send to (test only)" field that the USER
 * fills; the card passes it as extraBody.devRecipient (the only extra key this tool accepts).
 *
 * Before the card: GET config, then POST .../preview (renders; sends and writes nothing).
 * The card shows the rendered email in a sandboxed iframe. Limits mirror EmailMeLimits in
 * the API (TSAMM.SAHelp.Core/Services/EmailMe); the API re-checks them.
 */

export const EMAIL_ME_LIMITS = {
  subjectMax: 150,
  introMax: 2000,
  blocksMax: 50,
  headingMax: 200,
  paragraphMax: 4000,
  listItemsMax: 100,
  listItemMax: 500,
  columnsMax: 12,
  columnMax: 100,
  rowsMax: 500,
  cellMax: 1000,
} as const;

const L = EMAIL_ME_LIMITS;

const cell = z.union([z.string().max(L.cellMax), z.number(), z.null()]);

export const emailBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heading'), text: z.string().trim().min(1).max(L.headingMax) }),
  z.object({ type: z.literal('paragraph'), text: z.string().trim().min(1).max(L.paragraphMax) }),
  z.object({
    type: z.literal('list'),
    items: z.array(z.string().max(L.listItemMax)).min(1).max(L.listItemsMax),
  }),
  z.object({
    type: z.literal('table'),
    title: z.string().max(L.headingMax).optional(),
    columns: z.array(z.string().max(L.columnMax)).min(1).max(L.columnsMax),
    rows: z.array(z.array(cell).max(L.columnsMax)).max(L.rowsMax),
  }),
]);

export const emailMeInputSchema = z.object({
  subject: z.string().trim().min(1).max(L.subjectMax).describe(`Email subject (max ${L.subjectMax} characters).`),
  intro: z.string().trim().max(L.introMax).optional().describe('Optional opening paragraph, plain text.'),
  blocks: z
    .array(emailBlockSchema)
    .min(1)
    .max(L.blocksMax)
    .describe(
      `The report body as 1-${L.blocksMax} blocks: heading {text}, paragraph {text}, list {items[]}, table {title?, columns[] (max ${L.columnsMax}), rows[][] (max ${L.rowsMax} rows in all)}. Plain text only: no HTML, no markdown, no links.`
    ),
});

type EmailMeInput = z.infer<typeof emailMeInputSchema>;

/** Checks the rules a zod object cannot express per block: row width and total rows. */
export const emailBlocksProblem = (blocks: EmailMeInput['blocks']): string | null => {
  let rows = 0;
  for (const [i, block] of blocks.entries()) {
    if (block.type !== 'table') continue;
    rows += block.rows.length;
    const wide = block.rows.findIndex((r) => r.length > block.columns.length);
    if (wide >= 0) return `blocks.${i}.rows.${wide}: a row has more cells than the table has columns.`;
  }
  if (rows > L.rowsMax) return `The report has ${rows} table rows; the limit is ${L.rowsMax}. Summarize or split it.`;
  return null;
};

const NOT_AVAILABLE =
  'Emailing reports is not available on this SAHelp server yet. Offer to show the report here in the chat instead.';

const requestBody = (input: EmailMeInput) => ({
  subject: input.subject,
  ...(input.intro ? { intro: input.intro } : {}),
  blocks: input.blocks,
});

export const emailMeTool: AgentToolDef = {
  name: 'email_me_report',
  toolset: 'core',
  description:
    "Email a report to the signed-in user's OWN email address (never anyone else). Use when the user asks to email, send or save a report or data from this chat to themselves. Build it from data you already fetched with other tools, as structured blocks (headings, paragraphs, lists, tables) in plain text. Do not include more personal information than the user asked for. The user sees a preview and clicks Send; delivery is queued, so say it was queued, not delivered. Limits: 10 per hour, 30 per day.",
  inputSchema: emailMeInputSchema,
  method: 'POST',
  requiresConfirmation: true,
  extraBodyKeys: ['devRecipient'],
  notFoundMessage: NOT_AVAILABLE,
  prepare: async (input: EmailMeInput, ctx): Promise<AgentPrepared> => {
    const problem = emailBlocksProblem(input.blocks);
    if (problem) return { error: problem };

    const config = await ctx.get('/email-me/config');
    if (!config.ok) return { error: config.status === 404 ? NOT_AVAILABLE : config.message };
    const emailOverride = {
      allowed: config.data?.overrideAllowed === true,
      allowedDomains: Array.isArray(config.data?.allowedDomains)
        ? config.data.allowedDomains.map((d: unknown) => String(d).toLowerCase())
        : [],
    };

    const preview = await ctx.preview('/email-me/preview', requestBody(input));
    if (!preview.ok) return { error: preview.status === 404 ? NOT_AVAILABLE : preview.message };
    return {
      emailOverride,
      emailPreview: {
        subject: String(preview.data?.subject ?? input.subject),
        html: String(preview.data?.html ?? ''),
        recipientMasked: preview.data?.recipientMasked ? String(preview.data.recipientMasked) : null,
      },
    };
  },
  buildRequest: (input: EmailMeInput) => ({ path: '/email-me', body: requestBody(input) }),
  confirm: {
    title: (input: EmailMeInput) => `Email "${input.subject}" to yourself`,
    confirmLabel: 'Send',
    sideEffects: (_input, p) => [
      `EMAILS this report to your own SAHelp email address${
        p?.emailPreview?.recipientMasked ? ` (${p.emailPreview.recipientMasked})` : ''
      }. Nobody else receives it.`,
      'It is queued for delivery and can take a few minutes to arrive.',
    ],
    fields: (input: EmailMeInput, p) => [
      { label: 'Subject', value: String(p?.emailPreview?.subject ?? input.subject) },
      { label: 'Contents', value: `${input.blocks.length} block(s)` },
    ],
  },
  selectResult: (r) => ({
    queued: true,
    recipientMasked: r?.recipientMasked ?? null,
    overridden: r?.overridden ?? false,
  }),
};

/** Client-side check for the test-only recipient field (the API re-checks it). */
export const devRecipientProblem = (value: string, allowedDomains: string[]): string | null => {
  const email = value.trim();
  if (!email) return null;
  if (!/^[^\s@<>(),;:"]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(email)) return 'Enter a valid email address.';
  const domain = email.split('@').pop()!.toLowerCase();
  if (allowedDomains.length && !allowedDomains.includes(domain)) {
    return `Test reports can only go to: ${allowedDomains.join(', ')}.`;
  }
  return null;
};
